import type {
  GroupDetail,
  GroupEntry,
  GroupMember,
} from '../../services/api/types.gen';

export type EntryRef = { kind: 'user' | 'role'; id: string };

export function entriesFromDetail(detail: GroupDetail): EntryRef[] {
  return detail.entries.map(entry => ({ kind: entry.kind, id: entry.id }));
}

export function addEntry(entries: EntryRef[], entry: EntryRef): EntryRef[] {
  return entries.some(e => e.kind === entry.kind && e.id === entry.id)
    ? entries
    : [...entries, entry];
}

export function removeEntry(entries: EntryRef[], entry: EntryRef): EntryRef[] {
  return entries.filter(e => !(e.kind === entry.kind && e.id === entry.id));
}

export type RosterSection = { role: GroupEntry | null; people: GroupMember[] };

/** One section per role, then "Added individually". A person who is both a
 *  holder and a manual row appears once, under the role — the sync would put
 *  a removed holder back, so their row carries no remove control. */
export function buildRoster(detail: GroupDetail): RosterSection[] {
  const roles = detail.entries.filter(e => e.kind === 'role');
  const sections: RosterSection[] = roles.map(role => ({
    role,
    people: detail.members.filter(m => m.via_roles.includes(role.id)),
  }));
  const manualOnly = detail.members.filter(
    m => m.added_individually && m.via_roles.length === 0
  );
  return [...sections, { role: null, people: manualOnly }];
}

export function peopleCount(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'}`;
}

export function rosterHeadline(detail: GroupDetail): string {
  return `${peopleCount(detail.members.length)} in the conversation`;
}

/** The channel to open from a drawer, or null. Chat shows a channel to its
 *  members only, so a non-member administrator gets no conversation to open. */
export function conversationChannelId(
  detail: GroupDetail,
  userId: string | undefined
): string | null {
  if (!detail.channel_id || !userId) return null;
  return detail.members.some(member => member.user_id === userId)
    ? detail.channel_id
    : null;
}
